"use client";
import { useEffect } from "react";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Container,
	IconButton,
	Stack,
	Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
	documentToReactComponents,
	Options,
} from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES, Document } from "@contentful/rich-text-types";
import Image from "next/image";

import styles from "./Footer.module.css";
import Link from "next/link";
import { Button } from "@/app/components/";
import { MuiIcon } from "@/app/components/studio/MuiIcon";
import {
	pushNavigationClickEvent,
	pushInvocaPageLoadEvent,
} from "@/lib/utils/dataLayerHelpers";
import { useAppContext } from "@/app/contextProviders/app-context";
import { App, Page, NavigationLink } from "@/lib/__generated/contentful-sdk";
import { ONE_TRUST_TOGGLE } from "@/lib/constants";
import { PrivacyOptions } from "@/app/assets/aspenmarketing/PrivacyOptions";
import { formatPhoneNumber } from "@/lib/utils/format-phone-number";
interface FooterProps {
	footerData: App["footer"];
	useSelectedOfficePhoneNumber?: boolean;
}

// Check if link is One Trust to open One Trust modal
const isOneTrustLink = (navigationLink: NavigationLink | null) => {
	return (
		navigationLink &&
		typeof navigationLink === "object" &&
		navigationLink !== null &&
		"externalUrl" in navigationLink &&
		navigationLink.externalUrl?.includes(ONE_TRUST_TOGGLE)
	);
};

// Type guard to check if an item is a Page
function isPage(item: Page | NavigationLink): item is Page {
	return item.__typename === "Page";
}

// Helper to get the href from either Page or NavigationLink
function getHref(item: Page | NavigationLink): string {
	if (isPage(item)) {
		return item.path ?? "";
	}
	// NavigationLink
	if (item?.externalUrl?.includes(ONE_TRUST_TOGGLE)) {
		return "#";
	}
	return item.externalUrl ?? item.pageData?.path ?? "";
}

// Helper to get the display text from either Page or NavigationLink
function getDisplayText(item: Page | NavigationLink): string {
	if (isPage(item)) {
		return item.breadcrumbTitle ?? item.title ?? "";
	}
	// NavigationLink
	return item.linkText ?? "";
}

const makeRichTextOptions = (
	onLinkClick: (uri: string, linkText: string) => void,
): Options => ({
	renderNode: {
		[BLOCKS.PARAGRAPH]: (_node, children) => (
			<Typography
				variant="bodyXS"
				sx={{ color: "primary.contrastText" }}
				align="center"
				component="p"
			>
				{children}
			</Typography>
		),
		[INLINES.HYPERLINK]: (node, children) => {
			const uri = node.data.uri as string;
			const linkText = (node.content[0] as { value?: string })?.value ?? "";
			return (
				<a href={uri} onClick={() => onLinkClick(uri, linkText)}>
					{children}
				</a>
			);
		},
	},
});

const Footer = ({
	footerData: data,
	useSelectedOfficePhoneNumber,
}: FooterProps) => {
	const { selectedOffice } = useAppContext();

	// Prefer the selected office number when the flag is set,
	// falling back to the footer CMS number or the default.
	const footerPhoneNumber =
		formatPhoneNumber(data?.contactNumber) ?? "(800) 277-3633";
	const contactNumber = useSelectedOfficePhoneNumber
		? (formatPhoneNumber(selectedOffice?.phoneNumber) ?? footerPhoneNumber)
		: footerPhoneNumber;

	const handleFooterLinkClick = (linkUrl: string, linkText: string) => {
		pushNavigationClickEvent({
			destinationUrl: linkUrl,
			linkClickLocation: "footer",
			linkText,
			facilityCode: selectedOffice?.code,
			facilityName: selectedOffice?.name,
		});
	};

	const richTextOptions = makeRichTextOptions(handleFooterLinkClick);

	useEffect(() => {
		if (contactNumber) {
			const telHref = `tel:${contactNumber}`;
			const formattedPhone = telHref
				.replace(/\D/g, "")
				.replace(/^1/, "")
				.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");

			pushInvocaPageLoadEvent({
				invocaPhoneNumber: formattedPhone,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [contactNumber, selectedOffice?.code]);

	if (!data) return null;

	return (
		<footer className={styles.footer} data-testid="footer">
			<Container maxWidth="lg">
				<div className={styles.mobileLinks}>
					{data.navigationGroupsCollection?.items
						.filter((group) => group?.navigationGroupLink)
						.map((group) => {
							if (!group) return null;
							const groupLink = group.navigationGroupLink!;
							return (
								<Accordion key={groupLink.sys.id}>
									<AccordionSummary
										expandIcon={
											<MuiIcon
												className={styles.expandIcon}
												iconName="expand_more"
											/>
										}
									>
										<Typography
											variant="bodyLBold"
											sx={{ color: "primary.contrastText" }}
											component="span"
										>
											{groupLink.breadcrumbTitle}
										</Typography>
									</AccordionSummary>
									<AccordionDetails>
										<Stack spacing={1.5}>
											{group.navigationLinksForGroupCollection?.items
												.filter(Boolean)
												.map((link) => {
													if (!link) return null;
													const href = getHref(link);
													const displayText = getDisplayText(link);
													if (!href) return null;
													const isOneTrust =
														!isPage(link) && isOneTrustLink(link);
													return (
														<Link
															key={link.sys.id}
															href={href}
															prefetch={false}
															className={styles.link}
															title={displayText}
															onClick={(e) => {
																if (isOneTrust) {
																	e.preventDefault();
																	(
																		globalThis as any
																	)?.OneTrust?.ToggleInfoDisplay();
																}
																handleFooterLinkClick(href, displayText);
															}}
														>
															<Typography
																variant="bodyM"
																sx={{ color: "primary.contrastText" }}
																component="div"
																style={{
																	...(isOneTrust && {
																		display: "inline-flex",
																		alignItems: "center",
																		whiteSpace: "nowrap",
																	}),
																}}
															>
																{isOneTrust && <PrivacyOptions />}
																{displayText}
															</Typography>
														</Link>
													);
												})}
										</Stack>
									</AccordionDetails>
								</Accordion>
							);
						})}
				</div>

				<div className={styles.desktopLinks}>
					{data.navigationGroupsCollection?.items
						.filter((group) => group?.navigationGroupLink)
						.map((group) => {
							if (!group) return null;
							const groupLink = group.navigationGroupLink!;
							const groupHref = groupLink.path ?? "";
							const groupTitle = groupLink.breadcrumbTitle ?? "";

							return (
								<Stack
									key={groupLink.sys.id}
									className={styles.desktopLinksColumn}
								>
									{groupHref ? (
										<Link
											href={groupHref}
											prefetch={false}
											className={styles.link}
											title={groupTitle}
											onClick={() => {
												handleFooterLinkClick(groupHref, groupTitle);
											}}
										>
											<Typography
												variant="bodyLBold"
												sx={{ color: "primary.contrastText", mb: 2 }}
												component="div"
											>
												{groupTitle}
											</Typography>
										</Link>
									) : (
										<Typography
											variant="bodyLBold"
											sx={{ color: "primary.contrastText", mb: 2 }}
											component="div"
										>
											{groupTitle}
										</Typography>
									)}
									{group.navigationLinksForGroupCollection?.items
										.filter(Boolean)
										.map((link) => {
											if (!link) return null;
											const href = getHref(link);
											const displayText = getDisplayText(link);
											if (!href) return null;
											const isOneTrust = !isPage(link) && isOneTrustLink(link);
											return (
												<Link
													key={link.sys.id}
													href={href}
													prefetch={false}
													className={styles.link}
													title={displayText}
													onClick={(e) => {
														if (isOneTrust) {
															e.preventDefault();
															(
																globalThis as any
															)?.OneTrust?.ToggleInfoDisplay();
														}
														handleFooterLinkClick(href, displayText);
													}}
												>
													<Typography
														variant="bodyM"
														component="div"
														sx={{
															color: "primary.contrastText",
															mb: 1.5,
															...(isOneTrust && {
																display: "inline-flex",
																alignItems: "center",
																whiteSpace: "nowrap",
															}),
														}}
													>
														{isOneTrust && <PrivacyOptions />}
														{displayText}
													</Typography>
												</Link>
											);
										})}
								</Stack>
							);
						})}
				</div>

				<Stack sx={{ alignItems: "center", my: 4 }}>
					<Typography
						variant="bodyLBold"
						sx={{ color: "primary.contrastText", mb: 1 }}
						component="div"
					>
						Follow us
					</Typography>
					<Grid
						spacing={2}
						container
						sx={{ alignContent: "center", justifyContent: "center", mb: 2 }}
					>
						{data.socialNetworksCollection?.items
							.filter(Boolean)
							.map((item, index) => {
								if (!item) return null;
								const socialItem = item as Page | NavigationLink;
								const href = getHref(socialItem);
								if (!href) return null;
								const imageUrl = isPage(socialItem)
									? socialItem.image?.url
									: socialItem.linkIcon?.url;
								const imageTitle = isPage(socialItem)
									? socialItem.image?.title
									: socialItem.linkIcon?.title;

								return (
									<div key={socialItem.sys.id || `social-${index}`}>
										<IconButton
											aria-label={imageTitle ?? "social-link"}
											href={href}
											title={imageTitle ?? "social-link"}
											target="_blank"
											rel="noopener noreferrer"
										>
											<Image
												src={imageUrl ?? ""}
												alt={imageTitle ?? "social icon"}
												width={32}
												height={32}
											/>
										</IconButton>
									</div>
								);
							})}
					</Grid>
					<Button
						startIcon={<MuiIcon iconName="phone" />}
						href={`tel:${contactNumber}`}
						variant="tertiaryHC"
						title={`tel:${contactNumber}`}
						className={styles.phoneNumber}
					>
						<Typography
							variant="headingXS"
							component="span"
							sx={{ color: "text.light" }}
						>
							{contactNumber}
						</Typography>
					</Button>
				</Stack>
				<Typography
					variant="bodyXS"
					sx={{ color: "primary.contrastText", mb: 2 }}
					align="center"
					component="p"
				>
					{data.copyright}
				</Typography>
				{data.about &&
					documentToReactComponents(
						data.about.json as Document,
						richTextOptions,
					)}
			</Container>
		</footer>
	);
};

export default Footer;
